/**
 * CssUploadPanel — extracted Section A of MarpTemplateSettings.
 *
 * Form: slug + description + scope + raw CSS string. Posts
 * ``POST /api/v1/templates/marp/upload-css`` (see
 * ``@/features/sources/marp/api``) and lists the user/org customs
 * underneath so the user can see what they already have.
 *
 * Kept in a dedicated file (June 11, 2026 split): the legacy 1212-LoC
 * ``MarpTemplateSettings.tsx`` monolith made the form impossible to
 * reason about without scrolling past two unrelated sections.
 */

import { Loader2, Plus, Save, Trash2 } from "lucide-react";
import * as React from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import type { I18n } from "@lingui/core";

import { Button, SettingsPanel, cn, toast } from "@ando/ui";

import {
  listMarpTemplates,
  uploadCustomCss,
  type MarpCustomTemplate,
  type MarpTemplateListResponse,
  type MarpTemplateOwnerScope,
} from "@/features/sources/marp/api";
import { marpTemplatesQueryKey } from "@/features/sources/marp/useMarpTemplates";

import { FieldLabel, ScopeRadio } from "./shared";

// Slug validation mirrors the BE constraint: lowercase + kebab-case,
// 3-48 chars. We surface the rule inline so the user sees the error
// before round-tripping.
const SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;
const SLUG_MIN = 3;
const SLUG_MAX = 48;

function validateSlug(slug: string, i18n: I18n): string | null {
  const trimmed = slug.trim();
  if (trimmed.length === 0) return i18n._(msg`Slug obbligatorio`);
  if (trimmed.length < SLUG_MIN)
    return i18n._(msg`Almeno ${SLUG_MIN} caratteri`);
  if (trimmed.length > SLUG_MAX)
    return i18n._(msg`Massimo ${SLUG_MAX} caratteri`);
  if (!SLUG_PATTERN.test(trimmed))
    return i18n._(
      msg`Usa solo lettere minuscole, numeri e trattini (es. \`\`my-blue-deck\`\`)`,
    );
  return null;
}

export interface CssUploadPanelProps {
  /**
   * Imperative ref — when set, the parent (PptxExtractPanel) can prefill
   * the form with the suggested slug + extracted CSS after a successful
   * PPTX extraction. ``null`` if the consumer doesn't need it.
   */
  readonly handleRef?: React.MutableRefObject<CssUploadPanelHandle | null>;
}

export interface CssUploadPanelHandle {
  /** Prefill the form with the suggested slug + extracted CSS. */
  prefill(slug: string, css: string): void;
}

export const CssUploadPanel: React.FC<CssUploadPanelProps> = ({
  handleRef,
}) => {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  const [slug, setSlug] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [scope, setScope] = React.useState<MarpTemplateOwnerScope>("user");
  const [css, setCss] = React.useState("");

  const listQuery = useQuery<MarpTemplateListResponse>({
    queryKey: marpTemplatesQueryKey(),
    queryFn: listMarpTemplates,
    staleTime: 60_000,
    retry: 1,
  });

  const uploadMutation = useMutation({
    mutationFn: () =>
      uploadCustomCss({
        slug: slug.trim(),
        css,
        description: description.trim() || undefined,
        scope,
      }),
    onSuccess: (res) => {
      toast.success(i18n._(msg`Template ${res.slug} salvato`));
      void queryClient.invalidateQueries({ queryKey: marpTemplatesQueryKey() });
      setSlug("");
      setCss("");
    },
    onError: (err) => {
      const reason =
        err instanceof Error ? err.message : i18n._(msg`Errore sconosciuto`);
      toast.error(i18n._(msg`Salvataggio fallito: ${reason}`));
    },
  });

  // Expose imperative prefill — the parent shell wires this so the PPTX
  // extractor can hand its result over without coupling state.
  React.useImperativeHandle(
    handleRef,
    () => ({
      prefill(nextSlug, nextCss) {
        setSlug(nextSlug);
        setCss(nextCss);
        if (typeof document !== "undefined") {
          const anchor = document.getElementById("marp-templates-section-a");
          anchor?.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      },
    }),
    [],
  );

  const slugError = React.useMemo(() => validateSlug(slug, i18n), [slug, i18n]);
  const cssEmpty = css.trim().length === 0;
  const canSubmit =
    slugError === null && !cssEmpty && !uploadMutation.isPending;

  const customs: ReadonlyArray<MarpCustomTemplate> =
    listQuery.data?.customs ?? [];

  return (
    <SettingsPanel
      variant="card"
      id="marp-templates-section-a"
      aria-labelledby="marp-tpl-section-a-title"
      icon={Plus}
      title={
        <span id="marp-tpl-section-a-title">
          <Trans>Carica CSS</Trans>
        </span>
      }
      description={<Trans>Incolla il CSS Marp pronto e dagli un nome.</Trans>}
    >
      <div className="flex flex-col gap-3">
        <FieldLabel htmlFor="marp-tpl-slug" required>
          <Trans>Slug</Trans>
        </FieldLabel>
        <input
          id="marp-tpl-slug"
          type="text"
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          placeholder={i18n._(msg`es. blue-pitch-2026`)}
          spellCheck={false}
          className={cn(
            "rounded-md border bg-card px-2.5 py-1.5 text-[13px] text-ink-1",
            "focus:outline-none focus:ring-2 focus:ring-ando-orange/40",
            slug.length > 0 && slugError !== null
              ? "border-destructive/60"
              : "border-hairline",
          )}
        />
        {slug.length > 0 && slugError !== null ? (
          <p role="alert" className="text-[11px] text-destructive">
            {slugError}
          </p>
        ) : (
          <p className="text-[11px] text-ink-3">
            <Trans>
              Lowercase + trattini. Verrà mostrato nel picker del deck.
            </Trans>
          </p>
        )}

        <FieldLabel htmlFor="marp-tpl-desc">
          <Trans>Descrizione</Trans>
        </FieldLabel>
        <input
          id="marp-tpl-desc"
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={i18n._(msg`Opzionale, max 140 caratteri`)}
          maxLength={140}
          className="rounded-md border border-hairline bg-card px-2.5 py-1.5 text-[13px] text-ink-1 focus:outline-none focus:ring-2 focus:ring-ando-orange/40"
        />

        <fieldset className="flex flex-col gap-1.5">
          <legend className="font-medium text-[11px] text-ink-3 uppercase tracking-wide">
            <Trans>Scope</Trans>
          </legend>
          <div className="flex gap-2">
            <ScopeRadio
              name="marp-tpl-scope"
              value="user"
              checked={scope === "user"}
              onChange={() => setScope("user")}
              label={i18n._(msg`Solo per me`)}
            />
            <ScopeRadio
              name="marp-tpl-scope"
              value="org"
              checked={scope === "org"}
              onChange={() => setScope("org")}
              label={i18n._(msg`Tutta l'organizzazione`)}
            />
          </div>
        </fieldset>

        <FieldLabel htmlFor="marp-tpl-css" required>
          <Trans>CSS Marp</Trans>
        </FieldLabel>
        <textarea
          id="marp-tpl-css"
          value={css}
          onChange={(e) => setCss(e.target.value)}
          rows={20}
          spellCheck={false}
          placeholder="/* @theme my-brand */&#10;section { background: #FAF7F2; color: #1A1A1A; }&#10;h1 { color: #FF6B35; }"
          className="rounded-md border border-hairline bg-card px-2.5 py-1.5 font-mono text-[11.5px] text-ink-1 focus:outline-none focus:ring-2 focus:ring-ando-orange/40"
        />

        <div className="flex items-center justify-end">
          <Button
            type="button"
            onClick={() => uploadMutation.mutate()}
            disabled={!canSubmit}
          >
            {uploadMutation.isPending ? (
              <Loader2 size={14} strokeWidth={1.75} className="animate-spin" />
            ) : (
              <Save size={14} strokeWidth={1.75} />
            )}
            {uploadMutation.isPending
              ? i18n._(msg`Salvataggio…`)
              : i18n._(msg`Salva template`)}
          </Button>
        </div>

        <div className="border-hairline border-t pt-3">
          <h3 className="mb-2 font-medium text-[11px] text-ink-3 uppercase tracking-wide">
            <Trans>Template esistenti</Trans>
          </h3>
          {listQuery.isLoading ? (
            <p className="text-[12px] text-ink-3">
              <Trans>Caricamento…</Trans>
            </p>
          ) : customs.length === 0 ? (
            <p className="text-[12px] text-ink-3">
              <Trans>
                Non hai ancora caricato nessun template personalizzato.
              </Trans>
            </p>
          ) : (
            <ul className="flex flex-col gap-1.5">
              {customs.map((row) => (
                <li
                  key={`${row.scope}/${row.slug}`}
                  className="flex items-center gap-2 rounded-md border border-hairline bg-card/60 px-2.5 py-2"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-[12.5px] text-ink-1">
                      {row.slug}
                    </p>
                    {row.description ? (
                      <p className="truncate text-[11px] text-ink-3">
                        {row.description}
                      </p>
                    ) : null}
                  </div>
                  <span
                    className={cn(
                      "shrink-0 rounded-full border px-2 py-0.5 font-medium text-[10px] uppercase tracking-wide",
                      row.scope === "org"
                        ? "border-ando-orange/30 bg-ando-orange-soft text-ando-orange"
                        : "border-hairline bg-cream-deep/40 text-ink-3",
                    )}
                  >
                    {row.scope === "org"
                      ? i18n._(msg`Org`)
                      : i18n._(msg`Personale`)}
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    disabled
                    aria-label={i18n._(msg`Elimina template ${row.slug}`)}
                    title={i18n._(
                      msg`L'eliminazione arriverà in una prossima release`,
                    )}
                  >
                    <Trash2 size={13} strokeWidth={1.75} />
                  </Button>
                </li>
              ))}
            </ul>
          )}
          {listQuery.isError ? (
            <p
              role="alert"
              className="mt-2 rounded-md border border-destructive/40 bg-destructive/5 p-2 text-[11.5px] text-destructive"
            >
              <Trans>
                Impossibile caricare la lista dei template — riprova fra un
                istante.
              </Trans>
            </p>
          ) : null}
        </div>
      </div>
    </SettingsPanel>
  );
};
CssUploadPanel.displayName = "MarpTemplateSettings.CssUploadPanel";
